// Which route-list entry the rep picked in the Create-invoice customer list, shared by the form
// preview and createInvoice so the two ALWAYS agree.
//
// Why this exists: the route list can hold two entries for ONE dealer, sharing one numeric id: the
// admin-assigned copy (it carries the dealer uid in _supabaseKey) and a rep-side duplicate written back
// by the route-list merge/backfill (no uid). `list.find(a => a.id === id)` returned whichever came first,
// so the rep could be unable to pick the entry that carries the customer's discount.
//
// Identity is the dealer UID only (resolveDiscountKey). Company NAMES are compared here for exactly two
// cosmetic purposes, a warning and hiding a pure duplicate in the picker, and a name match NEVER selects
// a different entry, never supplies a key and never produces a discount. They live in this file (not in
// invoiceDiscount.js) so that module stays free of any name matching.
import { resolveDiscountKey } from "./invoiceDiscount";

export const normCompany = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

// list + the picked id (a number or the select's string value) ->
//   entry           the chosen entry (null when nothing matches)
//   key             resolveDiscountKey(entry): the dealer uid, or null when it is not linked
//   linkedDuplicate another entry with the same normalised company name but a DIFFERENT id that IS linked
//                   to a dealer account; set only when the chosen entry is not linked. A WARNING aid only.
// Among entries with the same id the first one that carries a key wins; a stable first-in-list tie-break
// applies when none (or all) have one.
export function resolveCustomerEntry(list, rawId) {
  const none = { entry: null, key: null, linkedDuplicate: null };
  const all = Array.isArray(list) ? list : [];
  const id = parseInt(rawId, 10);
  if (!Number.isFinite(id)) return none;
  const sameId = all.filter((a) => a && a.id === id);
  if (!sameId.length) return none;
  const entry = sameId.find((a) => resolveDiscountKey(a)) || sameId[0];
  const key = resolveDiscountKey(entry);
  let linkedDuplicate = null;
  if (!key) {
    const name = normCompany(entry.company);
    if (name) {
      linkedDuplicate = all.find((a) => a && a !== entry && a.id !== entry.id && normCompany(a.company) === name && resolveDiscountKey(a)) || null;
    }
  }
  return { entry, key, linkedDuplicate };
}

// The options of the customer <select>: one per (id, normalised company), preferring the entry that
// carries the dealer uid, in first-seen order. Entries with the same id but a different company are all
// kept (nothing is hidden that a rep could have meant).
export function customerPickerOptions(list) {
  const all = Array.isArray(list) ? list : [];
  const order = [];
  const chosen = new Map();
  all.forEach((a) => {
    if (!a) return;
    const k = String(a.id) + "|" + normCompany(a.company);
    if (!chosen.has(k)) { chosen.set(k, a); order.push(k); return; }
    if (!resolveDiscountKey(chosen.get(k)) && resolveDiscountKey(a)) chosen.set(k, a);
  });
  return order.map((k) => chosen.get(k));
}
